// src/hooks/useOrders.ts
// 
// ✨ ИЗМЕНЕНО: Удален отдельный метод updateInvoiceFlag
// Теперь флаг счета обновляется через существующий updateOrderStatus
//
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { ordersApi, Order } from '@/services/api/orders';

interface OrdersParams {
  page?: number;
  per_page?: number;
  status?: string;
  payment_status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  min_amount?: number;
  max_amount?: number;
}

interface OrdersResponse {
  count: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  has_next: boolean;
  has_previous: boolean;
  results: Order[];
}

interface OrderDetail {
  order: Order;
  additional_info: {
    days_since_created: number;
    payment_expired: boolean;
    order_items_count: number;
    formatted_address: string;
  };
}

interface AnalyticsData {
  date_from: string;
  date_to: string;
  summary: {
    total_orders: number;
    completed_orders: number;
    total_revenue: number;
    average_order_value: number;
  };
  status_distribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  top_customers: Array<{
    customer_name: string;
    customer_phone: string;
    orders_count: number;
    total_spent: number;
  }>;
}

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersData, setOrdersData] = useState<OrdersResponse | null>(null);
  const [currentOrder, setCurrentOrder] = useState<OrderDetail | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Загрузка списка заказов
  const loadOrders = async (params?: OrdersParams) => {
    setOrdersLoading(true);
    try {
      const response = await ordersApi.getOrders(params);
      setOrdersData(response.data.data);
      setOrders(response.data.data.results);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки заказов';
      message.error(errorMsg);
      console.error('Error loading orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Загрузка деталей заказа
  const loadOrderDetail = async (orderId: number) => {
    setOrderDetailLoading(true);
    try {
      const response = await ordersApi.getOrder(orderId);
      setCurrentOrder(response.data.data);
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки деталей заказа';
      message.error(errorMsg);
      console.error('Error loading order detail:', error);
      throw error;
    } finally {
      setOrderDetailLoading(false);
    }
  };

  // Обновление статуса заказа
  // ✨ ИЗМЕНЕНО: Теперь принимает опциональный параметр invoiceSentFlag
  const updateOrderStatus = async (
    orderId: number, 
    status: string, 
    comment?: string,
    invoiceSentFlag?: boolean  // ✨ НОВЫЙ ПАРАМЕТР
  ) => {
    setLoading(true);
    try {
      const payloadData: any = { status, comment };
      
      // Автоматическая смена payment_status при confirmed
      if (status === 'confirmed') {
        payloadData.payment_status = 'paid';
      }
      
      // ✨ НОВОЕ: Если передан флаг счета - добавляем в payload
      if (invoiceSentFlag !== undefined) {
        payloadData.invoice_sent_flag = invoiceSentFlag;
      }
      
      const response = await ordersApi.updateOrderStatus(orderId, payloadData);
      message.success('Статус заказа обновлен');
      
      // Обновляем данные в состоянии
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { 
          ...order, 
          status: status as any,
          payment_status: payloadData.payment_status || order.payment_status,
          invoice_sent_flag: invoiceSentFlag !== undefined ? invoiceSentFlag : order.invoice_sent_flag  // ✨ НОВОЕ
        } : order
      ));
      
      // Если загружены детали этого заказа, обновляем их тоже
      if (currentOrder && currentOrder.order.id === orderId) {
        setCurrentOrder(prev => prev ? {
          ...prev,
          order: { 
            ...prev.order, 
            status: status as any,
            payment_status: payloadData.payment_status || prev.order.payment_status,
            invoice_sent_flag: invoiceSentFlag !== undefined ? invoiceSentFlag : prev.order.invoice_sent_flag  // ✨ НОВОЕ
          }
        } : null);
      }
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления статуса заказа';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ✨ НОВЫЙ МЕТОД: Обновление ТОЛЬКО флага счета (использует updateOrderStatus)
  const updateInvoiceFlag = async (orderId: number, invoiceSentFlag: boolean) => {
    setLoading(true);
    try {
      // Находим текущий заказ
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Заказ не найден');
      }
      
      // Используем существующий метод, передавая текущий статус и новый флаг
      await updateOrderStatus(orderId, order.status, undefined, invoiceSentFlag);
      
      // Сообщение об успехе
      const successMsg = invoiceSentFlag 
        ? 'Счет помечен как отправленный' 
        : 'Флаг отправки счета снят';
      message.success(successMsg);
      
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления флага счета';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Массовое обновление заказов
  const bulkUpdateOrders = async (orderIds: number[], status: string, comment?: string) => {
    setLoading(true);
    try {
      const payloadData: any = { order_ids: orderIds, status, comment };
      
      if (status === 'confirmed') {
        payloadData.payment_status = 'paid';
      }
      
      const response = await ordersApi.bulkUpdateOrders(payloadData);
      message.success(`Обновлено ${response.data.data.updated_count} заказов`);
      
      // Обновляем статусы в локальном состоянии
      setOrders(prev => prev.map(order => 
        orderIds.includes(order.id) ? { 
          ...order, 
          status: status as any,
          payment_status: payloadData.payment_status || order.payment_status
        } : order
      ));
      
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка массового обновления заказов';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Загрузка аналитики
  const loadAnalytics = async (params?: {
    date_from?: string;
    date_to?: string;
  }) => {
    setAnalyticsLoading(true);
    try {
      const response = await ordersApi.getAnalytics(params);
      setAnalytics(response.data.data);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки аналитики';
      message.error(errorMsg);
      console.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Очистка деталей заказа
  const clearOrderDetail = () => {
    setCurrentOrder(null);
  };

  // Перезагрузка текущих данных
  const refresh = () => {
    if (ordersData) {
      loadOrders({
        page: ordersData.current_page,
        per_page: ordersData.per_page
      });
    } else {
      loadOrders();
    }
  };

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    loadOrders();
  }, []);

  return {
    // Данные
    orders,
    ordersData,
    currentOrder,
    analytics,
    
    // Состояние загрузки
    loading,
    ordersLoading,
    orderDetailLoading,
    analyticsLoading,
    
    // Методы
    loadOrders,
    loadOrderDetail,
    updateOrderStatus,
    bulkUpdateOrders,
    updateInvoiceFlag,  // ✨ Упрощенный метод для изменения флага
    loadAnalytics,
    clearOrderDetail,
    refresh,
  };
};